import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CornerDownLeftIcon, FileTextIcon, ListChecksIcon, SearchIcon, UserIcon, WrenchIcon, XIcon } from 'lucide-react';
import { ProCustomer, ProJob, Quote } from '../types/pro';
import { QualityReport } from '../types/quality';
import { SearchResult, SearchResultKind, groupResults, searchEverything } from '../utils/search';
interface TopBarProps {
  customers: ProCustomer[];
  jobs: ProJob[];
  quotes: Quote[];
  qualityReports: QualityReport[];
  isAdmin: boolean;
  onSelectResult: (result: SearchResult) => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;
const KIND_ICON: Record<SearchResultKind, typeof UserIcon> = {
  customer: UserIcon,
  job: WrenchIcon,
  quote: FileTextIcon,
  quality: ListChecksIcon,
  page: CornerDownLeftIcon
};
export function TopBar({
  customers,
  jobs,
  quotes,
  qualityReports,
  isAdmin,
  onSelectResult
}: TopBarProps) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const results = useMemo(() => searchEverything({
    query,
    customers,
    jobs,
    quotes,
    qualityReports,
    isAdmin
  }), [query, customers, jobs, quotes, qualityReports, isAdmin]);
  const grouped = useMemo(() => groupResults(results), [results]);

  // Reset the highlighted row whenever the result set changes.
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  // Cmd/Ctrl+K focuses search from anywhere.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Close the dropdown on an outside click.
  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);
  const commit = (result: SearchResult) => {
    onSelectResult(result);
    setQuery('');
    setIsOpen(false);
    inputRef.current?.blur();
  };
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (results.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex(i => (i + 1) % results.length);
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(i => (i - 1 + results.length) % results.length);
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      commit(results[activeIndex]);
    }
  };
  const showDropdown = isOpen && query.trim().length >= 2;
  let flatIndex = -1;
  return <header className="sticky top-0 z-30 border-b border-stone-200 bg-white/90 backdrop-blur" data-id="element-521" data-mp="2laspftlprr3t">
      <div className="flex items-center gap-3 px-4 py-3 sm:px-6 lg:px-10" data-id="element-522" data-mp="2qcrt74ld8q1a">
        <span className="flex-shrink-0 text-base font-bold tracking-tight text-stone-900 lg:hidden" data-id="element-523" data-mp="2ubqxcmz9wiro">
          Lykke-EL
        </span>

        <div ref={containerRef} className="relative min-w-0 flex-1 lg:max-w-xl" data-id="element-524" data-mp="2z9sdp05be3zg">
          <label className="relative block" data-id="element-525" data-mp="3433mh6tosuu0">
            <span className="sr-only" data-id="element-526" data-mp="391ey9n3jlfev">Søg</span>
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" data-id="element-527" data-mp="39156btyxsy6j" />
            <input ref={inputRef} value={query} onChange={e => {
            setQuery(e.target.value);
            setIsOpen(true);
          }} onFocus={() => setIsOpen(true)} onKeyDown={handleKeyDown} placeholder="Søg kunder, opgaver, tilbud…" role="combobox" aria-expanded={showDropdown} aria-controls="global-search-results" className="w-full rounded-lg border border-stone-200 bg-stone-50 py-2 pl-9 pr-16 text-sm text-stone-900 outline-none transition-colors placeholder:text-stone-400 focus:border-transparent focus:bg-white focus:ring-2 focus:ring-brand-500" data-id="element-528" data-mp="3dyokktvmxayh" />
            {query ? <button onClick={() => {
            setQuery('');
            inputRef.current?.focus();
          }} aria-label="Ryd søgning" className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-700" data-id="element-529" data-mp="9oql1n8uoirm">
                <XIcon className="h-4 w-4" data-id="element-530" data-mp="1okrw29dor69u" />
              </button> : <kbd className="pointer-events-none absolute right-2.5 top-1/2 hidden -translate-y-1/2 rounded border border-stone-200 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-stone-400 sm:block" data-id="element-531" data-mp="1on4m79tlkmxz">
                ⌘K
              </kbd>}
          </label>

          <AnimatePresence data-id="element-532" data-mp="1jm722rp11itu">
            {showDropdown && <motion.div id="global-search-results" role="listbox" initial={{
            opacity: 0,
            y: -6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -6
          }} transition={{
            duration: 0.15,
            ease: EASE
          }} className="absolute left-0 right-0 top-full z-40 mt-2 max-h-[70vh] overflow-y-auto rounded-xl border border-stone-200 bg-white py-1.5 shadow-lg" data-id="element-533" data-mp="27azs67iywwgj">
                {results.length === 0 ? <p className="px-4 py-6 text-center text-sm text-stone-500" data-id="element-534" data-mp="2v1m07ysz2gqj">
                    Ingen resultater for “{query}”
                  </p> : grouped.map(group => <div key={group.group} data-id="element-535" data-mp="2zyizoqwcv8l2">
                      <h3 className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-stone-400" data-id="element-536" data-mp="2uy3pu9ba8j7u">
                        {group.group}
                      </h3>
                      <ul data-id="element-537" data-mp="2lxyifz639kto">
                        {group.items.map(result => {
                  flatIndex += 1;
                  const isActive = flatIndex === activeIndex;
                  const Icon = KIND_ICON[result.kind];
                  return <li key={result.id} role="option" aria-selected={isActive} data-id="element-538" data-mp="2792diazbwnxa">
                              <button onMouseEnter={() => setActiveIndex(results.indexOf(result))} onClick={() => commit(result)} className={`flex w-full items-center gap-3 px-3 py-2 text-left transition-colors ${isActive ? 'bg-brand-50' : 'hover:bg-stone-50'}`} data-id="element-539" data-mp="2777pgnsrgfoz">
                                <span className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${isActive ? 'bg-brand-100 text-brand-700' : 'bg-stone-100 text-stone-500'}`} data-id="element-540" data-mp="rqml8lxlzngn">
                                  <Icon className="h-4 w-4" data-id="element-541" data-mp="8riddghszzqk" />
                                </span>
                                <span className="min-w-0 flex-1" data-id="element-542" data-mp="37i0pgm070uq2">
                                  <span className="block truncate text-sm font-medium text-stone-900" data-id="element-543" data-mp="3cgbyiuinz178">
                                    {result.title}
                                  </span>
                                  {result.subtitle && <span className="block truncate text-xs text-stone-500" data-id="element-544" data-mp="20v811su2a4a6">
                                      {result.subtitle}
                                    </span>}
                                </span>
                                {result.reference && <span className="flex-shrink-0 font-mono text-xs text-stone-400" data-id="element-545" data-mp="2oq0xk6kclohs">
                                    {result.reference}
                                  </span>}
                              </button>
                            </li>;
                })}
                      </ul>
                    </div>)}
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </header>;
}